import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';
import { track } from '../../utils/tracking';
import ForSaleHeroBanner from '../../components/feature/ForSaleHeroBanner';
import ForSaleContentBlock from '../../components/feature/ForSaleContentBlock';

export default function Home() {
  useEffect(() => {
    track('page_view', { page: 'home' });
  }, []);

  return (
    <>
      <SEOHead
        title="Business Funding Solutions | Get Pre-Qualified Today"
        description="Explore business funding options tailored to your needs. Get pre-qualified in minutes with no credit impact. Working capital, term loans, equipment financing, and more."
        keywords="business funding, business loans, working capital, equipment financing, term loans, business line of credit"
        canonical="/"
      />

      <div className="min-h-screen flex flex-col bg-white">
        <Header />

        <main className="flex-grow">
          <ForSaleHeroBanner />
          {/* Hero Section */}
          <section className="relative min-h-[85vh] flex items-center overflow-hidden">
            <div className="absolute inset-0">
              <img 
                src="https://public.readdy.ai/ai/img_res/003ae08babf318da18df1980275d2ad6.jpg"
                alt="Business funding solutions for entrepreneurs"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/50"></div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full text-sm font-medium text-white mb-6">
                  <i className="ri-shield-check-line text-emerald-400"></i>
                  <span>No Credit Impact • 5 Minutes • Secure</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Find the Right
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">
                    Business Funding
                  </span>
                  for Your Goals
                </h1>
                
                <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
                  Get pre-qualified for business funding in minutes. Compare options from working capital to equipment financing. No impact on your credit score.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 sm:mb-10">
                  <Link
                    to="/business-funding"
                    onClick={() => track('cta_click', { location: 'hero', action: 'explore_funding' })}
                    className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-base sm:text-lg font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
                  >
                    <span className="block sm:hidden">Explore Funding</span>
                    <span className="hidden sm:block">Explore All Business Funding Options</span>
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                  <a
                    href="#education"
                    className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-sm text-white text-base sm:text-lg font-semibold rounded-lg border border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-book-open-line mr-2 flex-shrink-0"></i>
                    <span className="block sm:hidden">Learn More</span>
                    <span className="hidden sm:block">Learn What Lenders Evaluate</span>
                  </a>
                </div>

                <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-gray-300">
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-amber-400"></i>
                    <span>Quick Pre-Qualification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-shield-check-line text-emerald-400"></i>
                    <span>No Credit Impact</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-lock-line text-blue-400"></i>
                    <span>Secure & Confidential</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Funding Types Section */}
          <section className="py-16 sm:py-20 px-4 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12 sm:mb-16">
                <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full mb-4">
                  Funding Solutions
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                  Explore Your Funding Options
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Different business needs require different funding solutions. Find the right fit for your goals.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <Link
                  to="/funding/working-capital"
                  onClick={() => track('funding_type_click', { type: 'working_capital' })}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-amber-300 transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                    <i className="ri-funds-line text-amber-600 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Working Capital</h3>
                  <p className="text-gray-600 mb-4">Short-term financing for day-to-day operations, inventory, and cash flow needs.</p>
                  <div className="flex items-center text-amber-600 font-semibold text-sm">
                    Learn More
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </Link>

                <Link
                  to="/funding/line-of-credit"
                  onClick={() => track('funding_type_click', { type: 'line_of_credit' })}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-amber-300 transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                    <i className="ri-exchange-funds-line text-emerald-600 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Line of Credit</h3>
                  <p className="text-gray-600 mb-4">Revolving credit for flexible access to capital when you need it most.</p>
                  <div className="flex items-center text-amber-600 font-semibold text-sm">
                    Learn More
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </Link>

                <Link
                  to="/funding/term-loans"
                  onClick={() => track('funding_type_click', { type: 'term_loans' })}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-amber-300 transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                    <i className="ri-bank-line text-indigo-600 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Term Loans</h3>
                  <p className="text-gray-600 mb-4">Traditional loans with fixed amounts and repayment schedules for major investments.</p>
                  <div className="flex items-center text-amber-600 font-semibold text-sm">
                    Learn More
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </Link>

                <Link
                  to="/funding/equipment-financing"
                  onClick={() => track('funding_type_click', { type: 'equipment_financing' })}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-amber-300 transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                    <i className="ri-tools-line text-cyan-600 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Equipment Financing</h3>
                  <p className="text-gray-600 mb-4">Loans for purchasing business equipment with the equipment as collateral.</p>
                  <div className="flex items-center text-amber-600 font-semibold text-sm">
                    Learn More
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </Link>

                <Link
                  to="/funding/commercial-real-estate"
                  onClick={() => track('funding_type_click', { type: 'commercial_real_estate' })}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl hover:border-amber-300 transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-rose-100 to-pink-100 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                    <i className="ri-building-4-line text-rose-600 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Commercial Real Estate</h3>
                  <p className="text-gray-600 mb-4">Long-term financing for purchasing or refinancing commercial property.</p>
                  <div className="flex items-center text-amber-600 font-semibold text-sm">
                    Learn More
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </Link>

                <Link
                  to="/cities"
                  onClick={() => track('cta_click', { location: 'funding_grid', action: 'view_cities' })}
                  className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-6 hover:shadow-xl transition-all cursor-pointer group text-white"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                    <i className="ri-map-pin-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-2">Find Your City</h3>
                  <p className="text-white/90 mb-4">Explore funding options and resources specific to your location.</p>
                  <div className="flex items-center font-semibold text-sm">
                    Browse Cities
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 sm:py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Complete our quick pre-qualification assessment to see what funding options you may qualify for.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/prequalification"
                  onClick={() => track('cta_click', { location: 'bottom_cta', action: 'prequalification' })}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-lg font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Start Pre-Qualification
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <Link
                  to="/business-funding"
                  onClick={() => track('cta_click', { location: 'bottom_cta', action: 'learn_more' })}
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg border border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line mr-2"></i>
                  Learn About Funding
                </Link>
              </div>
            </div>
          </section>

          {/* For Sale Block */}
          <section className="py-8 px-4 bg-white">
            <div className="max-w-5xl mx-auto">
              <ForSaleContentBlock />
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
